import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config';

interface PendingProduct {
  pendingId: string;
  source: string;
  brandName: string;
  productName: string;
  thcContentMg: number;
  beverageFormat: string;
  priceUsd?: number;
  imageUrl?: string;
  sourceUrl: string;
  discoveredAt: string;
}

interface RunReport {
  type: string;
  startedAt: string;
  completedAt: string;
  summary: string;
}

export function AdminScraper() {
  const { authFetch } = useAuth();
  const [pending, setPending] = useState<PendingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState<string | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState<string | null>(null);
  const [lastReport, setLastReport] = useState<RunReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPending();
  }, [authFetch]);

  async function fetchPending() {
    try {
      const res = await authFetch(`${API_BASE}/v1/admin/pending`);
      if (res.ok) {
        const json = await res.json();
        setPending(json.data || []);
      }
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }

  async function runScraper(source: string) {
    setRunning(source);
    setError(null);
    setLastReport(null);

    try {
      const res = await authFetch(`${API_BASE}/v1/admin/scraper/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, limit: 50 }),
      });

      const json = await res.json();
      if (!res.ok) {
        setError(json.error?.message || 'Scraper run failed');
        return;
      }

      const data = json.data;
      let summary = '';
      if (source === 'shopify') {
        summary = `Scanned ${data.brandsScanned} brands, found ${data.shopifyBrandsFound} Shopify stores, ${data.productsNew} new products → ${data.pendingApproval} pending approval`;
      } else if (source === 'bedrock') {
        summary = `Processed ${data.productsProcessed} products, updated ${data.productsUpdated} with nutrition data`;
      } else if (source === 'search') {
        summary = `Searched ${data.productsSearched} products, discovered ${data.linksDiscovered} links, ${data.linksNew} new purchase links added`;
      }

      setLastReport({
        type: source,
        startedAt: data.startedAt,
        completedAt: data.completedAt,
        summary,
      });

      // Refresh pending list
      fetchPending();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(null);
    }
  }

  async function handleApprove(pendingId: string) {
    try {
      await authFetch(`${API_BASE}/v1/admin/pending/${pendingId}/approve`, { method: 'POST' });
      setPending((prev) => prev.filter((p) => p.pendingId !== pendingId));
    } catch {
      setError('Failed to approve product');
    }
  }

  async function handleReject(pendingId: string) {
    try {
      await authFetch(`${API_BASE}/v1/admin/pending/${pendingId}/reject`, { method: 'POST' });
      setPending((prev) => prev.filter((p) => p.pendingId !== pendingId));
    } catch {
      setError('Failed to reject product');
    }
  }

  async function handleBulkApprove(brandName: string) {
    const brandItems = pending.filter((p) => p.brandName === brandName);
    if (brandItems.length === 0) return;
    setBulkProcessing(brandName);
    let approved = 0;
    for (const item of brandItems) {
      try {
        await authFetch(`${API_BASE}/v1/admin/pending/${item.pendingId}/approve`, { method: 'POST' });
        approved++;
        // 1 second delay between approvals to prevent overwhelming DynamoDB streams
        await new Promise((r) => setTimeout(r, 1000));
      } catch {
        // Wait longer on failure (likely throttled)
        await new Promise((r) => setTimeout(r, 3000));
      }
    }
    setPending((prev) => prev.filter((p) => p.brandName !== brandName));
    setBulkProcessing(null);
  }

  async function handleBulkReject(brandName: string) {
    const brandItems = pending.filter((p) => p.brandName === brandName);
    if (brandItems.length === 0) return;
    setBulkProcessing(brandName);
    for (const item of brandItems) {
      try {
        await authFetch(`${API_BASE}/v1/admin/pending/${item.pendingId}/reject`, { method: 'POST' });
        await new Promise((r) => setTimeout(r, 100));
      } catch {
        // Continue
      }
    }
    setPending((prev) => prev.filter((p) => p.brandName !== brandName));
    setBulkProcessing(null);
  }

  // Group pending by brand
  const brandGroups: Record<string, PendingProduct[]> = {};
  for (const item of pending) {
    if (!brandGroups[item.brandName]) brandGroups[item.brandName] = [];
    brandGroups[item.brandName]!.push(item);
  }
  const sortedBrands = Object.keys(brandGroups).sort((a, b) => (brandGroups[b]?.length || 0) - (brandGroups[a]?.length || 0));

  return (
    <div>
      <h1>Scraper</h1>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {/* Run Controls */}
      <div className="admin-scraper-section">
        <h3>Run Scraper</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
          Trigger a scraper run manually. Results go to the approval queue below.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => runScraper('shopify')}
            disabled={running !== null}
          >
            {running === 'shopify' ? 'Scanning...' : '🛍️ Scan Shopify Stores'}
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={() => runScraper('bedrock')}
            disabled={running !== null}
          >
            {running === 'bedrock' ? 'Extracting...' : '🤖 AI Nutrition Extract'}
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={() => runScraper('search')}
            disabled={running !== null}
          >
            {running === 'search' ? 'Searching...' : '🔍 Find Purchase Links'}
          </button>
        </div>
      </div>

      {/* Last Run Report */}
      {lastReport && (
        <div className="admin-scraper-section" style={{ marginTop: '16px' }}>
          <h3>Last Run</h3>
          <div className="admin-stats" style={{ marginBottom: '8px' }}>
            <div className="admin-stat-card">
              <div className="stat-label">Source</div>
              <div className="stat-value" style={{ fontSize: '1rem' }}>{lastReport.type}</div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-label">Duration</div>
              <div className="stat-value" style={{ fontSize: '1rem' }}>
                {Math.round((new Date(lastReport.completedAt).getTime() - new Date(lastReport.startedAt).getTime()) / 1000)}s
              </div>
            </div>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{lastReport.summary}</p>
        </div>
      )}

      {/* Approval Queue */}
      <div className="admin-scraper-section" style={{ marginTop: '16px' }}>
        <h3>Pending Approval ({pending.length})</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
          New products discovered by the scraper. Review and approve to publish, or reject to discard.
        </p>

        {loading ? (
          <div className="admin-loading">Loading...</div>
        ) : pending.length === 0 ? (
          <div className="admin-placeholder">
            <p>No pending products. Run a scan to discover new drinks.</p>
          </div>
        ) : (
          <div>
            {sortedBrands.map((brandName) => {
              const items = brandGroups[brandName] || [];
              const isProcessing = bulkProcessing === brandName;
              return (
                <div key={brandName} style={{ marginBottom: '16px', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0 }}>{brandName} <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', fontWeight: 400 }}>({items.length} products)</span></h4>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        className="admin-btn admin-btn-primary"
                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                        onClick={() => handleBulkApprove(brandName)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Processing...' : `✓ Approve All (${items.length})`}
                      </button>
                      <button
                        className="admin-btn admin-btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                        onClick={() => handleBulkReject(brandName)}
                        disabled={isProcessing}
                      >
                        ✕ Reject All
                      </button>
                    </div>
                  </div>
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>THC</th>
                          <th>Format</th>
                          <th>Price</th>
                          <th>Source</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.pendingId}>
                            <td>
                              <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                                {item.productName}
                              </a>
                            </td>
                            <td>{item.thcContentMg}mg</td>
                            <td>{item.beverageFormat}</td>
                            <td>{item.priceUsd ? `$${item.priceUsd.toFixed(2)}` : '—'}</td>
                            <td>
                              <span className={`admin-badge ${item.source === 'shopify' ? 'admin-badge-green' : 'admin-badge-yellow'}`}>
                                {item.source}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  className="admin-btn admin-btn-primary"
                                  style={{ fontSize: '0.7rem', padding: '3px 6px' }}
                                  onClick={() => handleApprove(item.pendingId)}
                                >
                                  ✓
                                </button>
                                <button
                                  className="admin-btn admin-btn-secondary"
                                  style={{ fontSize: '0.7rem', padding: '3px 6px' }}
                                  onClick={() => handleReject(item.pendingId)}
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Scheduled Runs Info */}
      <div className="admin-scraper-section" style={{ marginTop: '16px' }}>
        <h3>Automated Schedule</h3>
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr><th>Pipeline</th><th>Schedule</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Shopify Scanner</td>
                <td>Daily 2am UTC</td>
                <td>Scans brand websites for new products via /products.json</td>
              </tr>
              <tr>
                <td>AI Nutrition Extract</td>
                <td>Weekly Mon 3am UTC</td>
                <td>Uses Bedrock Claude to extract calories, sugar, COA from product pages</td>
              </tr>
              <tr>
                <td>THC Drink List</td>
                <td>Daily 2am UTC</td>
                <td>Ingests new products from thcdrinklist.com</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
